import * as React from "react";
import { Slot } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap select-none",
    "rounded-md font-medium transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
    "active:translate-y-px",
  ],
  {
    variants: {
      variant: {
        primary:
          "bg-accent-strong text-white shadow-[0_1px_0_rgb(255_255_255/0.12)_inset,0_8px_24px_-10px_rgb(124_58_237/0.8)] hover:bg-accent-hover",
        secondary:
          "bg-surface-3 text-ink border border-line-strong hover:bg-[rgb(255_255_255/0.11)] hover:border-[rgb(255_255_255/0.2)]",
        outline:
          "border border-line-strong bg-transparent text-ink-2 hover:text-ink hover:border-line-accent hover:bg-accent-tint",
        ghost: "bg-transparent text-ink-2 hover:bg-surface-3 hover:text-ink",
        accent:
          "bg-accent-tint text-accent border border-[rgb(167_139_250/0.25)] hover:bg-accent-tint-strong hover:border-line-accent",
        danger:
          "bg-[rgb(240_100_122/0.12)] text-danger border border-[rgb(240_100_122/0.3)] hover:bg-[rgb(240_100_122/0.2)]",
        link: "text-accent underline-offset-4 hover:underline px-0 h-auto",
      },
      size: {
        xs: "h-7 px-2.5 text-xs rounded-sm",
        sm: "h-8 px-3 text-[13px]",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-[15px] rounded-lg",
        icon: "size-9",
        "icon-sm": "size-8 rounded-sm",
        "icon-xs": "size-7 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "secondary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : "button";
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
